/**
 * Ein Dokument ansehen — links der Beleg, rechts die Analyse.
 *
 * Nach dem Vault Muster gehoert der Bildschirm hier der Sache selbst: keine
 * Reiterzeile, kein Werkzeugumschalter, kein Mandantenkopf. Oben stehen nur
 * Rueckweg, Titel und Stand, darunter die geteilte Flaeche ueber die volle
 * Hoehe.
 *
 * Nebeneinander und nicht hintereinander, weil beim Lesen einer Analyse
 * staendig die Frage kommt "wo steht das". Wer dafuer die Ansicht wechseln
 * muss, prueft nicht nach. Die Trennlinie laesst sich ziehen und merkt sich
 * ihre Stelle.
 *
 * Die Datei kommt von `/api/dokumente/:id/datei` — entschluesselt beim Abruf,
 * nie im Klartext auf der Platte. Der Abruf wird protokolliert.
 */
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, ENTHAELT_NAME, standAnzeige, type Einlesung, type Stand, type Werkzeug } from '../lib/api'
import { Analysefeld } from '../teile/Analysefeld'
import { BwaDatenfeld } from '../teile/BwaDatenfeld'
import { Geteilt } from '../teile/Geteilt'
import { Rueckmeldekasten } from '../teile/Rueckmeldekasten'

interface Dokument {
  id: string
  art: string
  werkzeug: Werkzeug
  bezeichnung: string
  dateiname: string
  mime: string
  groesse_bytes: string
  stand: Stand
  einlesung?: Einlesung
  enthaelt: string[]
  art_aus_inhalt: boolean
  hinweis: string | null
  mandant_id: string
  mandant_name: string
  mandantennummer: number
}

function groesse(bytes: string): string {
  const zahl = Number(bytes)
  if (!Number.isFinite(zahl)) return ''
  return zahl < 1024 * 1024 ? `${Math.round(zahl / 1024)} kB` : `${(zahl / 1024 / 1024).toFixed(1)} MB`
}

export function DokumentSeite() {
  const { werkzeug, id } = useParams()
  // Kompakter Kopf des Monatsberichts: der Datenbereich darueber nur auf Wunsch.
  const [kompakt, setKompakt] = useState(false)
  const [datenSichtbar, setDatenSichtbar] = useState(false)

  const abfrage = useQuery({
    queryKey: ['dokument', id],
    queryFn: () => api.holen<{ dokument: Dokument }>(`/api/dokumente/${id}`),
    enabled: Boolean(id),
  })

  const dokument = abfrage.data?.dokument

  /* Der Titel des Fensters folgt der Sache. Wer mehrere Auswertungen
     nebeneinander offen hat, sieht in der Leiste, welche welche ist. */
  useEffect(() => {
    if (!dokument) return
    const vorher = document.title
    document.title = `${dokument.bezeichnung} — ${dokument.mandant_name}`
    return () => {
      document.title = vorher
    }
  }, [dokument])

  if (!werkzeug) return <Navigate to="/" replace />

  if (abfrage.isPending) return <div className="laedt">Wird geladen …</div>

  if (abfrage.isError || !dokument) {
    return (
      <div className="betrachter">
        <div className="betrachter-kopf">
          <Link className="zurueck" to={`/${werkzeug}/mandanten`}>
            ‹ Zurück
          </Link>
        </div>
        <div className="fehler" style={{ margin: 'var(--space-lg)' }}>
          {abfrage.error instanceof Error ? abfrage.error.message : 'Dieses Dokument gibt es nicht.'}
        </div>
      </div>
    )
  }

  const analysierbar =
    (dokument.werkzeug === 'bwa' && dokument.art === 'bwa') || (dokument.werkzeug === 'jahresabschluss' && dokument.art === 'jahresabschluss')

  return (
    <div className="betrachter">
      <div className="betrachter-kopf">
        <Link className="zurueck" to={`/${werkzeug}/mandanten/${dokument.mandant_id}`}>
          ‹ {dokument.mandant_name}
        </Link>

        <div className="betrachter-titel">
          <h1>{dokument.bezeichnung}</h1>
          <div className="nebensache">
            {dokument.dateiname} · {groesse(dokument.groesse_bytes)}
            {dokument.enthaelt.length > 1 &&
              ` · enthält ${dokument.enthaelt.map((e) => ENTHAELT_NAME[e] ?? e).join(', ')}`}
          </div>
        </div>

        {(() => {
              const anzeige = standAnzeige(dokument.stand, dokument.einlesung)
              return <div className={`stand ${anzeige}`}>{anzeige}</div>
            })()}
      </div>

      {(!dokument.art_aus_inhalt || dokument.hinweis) && (
        <p className="betrachter-hinweis">
          {dokument.hinweis ?? 'Einordnung stammt aus dem Titel, nicht aus dem Inhalt.'}
        </p>
      )}

      <Geteilt quelle={`/api/dokumente/${dokument.id}/datei`} titel={dokument.bezeichnung}>
        {/* Bei einem BWA Monatspaket steht ueber der Analyse, ob seine
            Zahlen im Bestand sind — die Analyse baut darauf auf. */}
        {dokument.werkzeug === 'bwa' && dokument.art === 'bwa' ? (
          <div className="feldstapel">
            <BwaDatenfeld dokumentId={dokument.id} versteckt={kompakt && !datenSichtbar} />
            <Analysefeld
              dokumentId={dokument.id}
              bwa
              beiKompakt={setKompakt}
              daten={{ sichtbar: datenSichtbar, umschalten: () => setDatenSichtbar((s) => !s) }}
            />
          </div>
        ) : dokument.werkzeug === 'jahresabschluss' && dokument.art === 'jahresabschluss' ? (
          <Analysefeld dokumentId={dokument.id} />
        ) : (
          /* Analysiert werden BWA Pakete und Jahresabschluesse. Fuer alles
             andere gibt es keinen Knopf, der dann nur einen Fehler meldet. */
          <aside className="analysefeld">
            <div className="analysefeld-kopf">
              <div className="analysefeld-titel">
                <h2>Analyse</h2>
              </div>
            </div>
            <div className="analysefeld-koerper">
              <div className="analysefeld-leer">
                <p className="analysefeld-leer-titel">Wird nicht analysiert</p>
                <p className="analysefeld-leer-text">
                  {dokument.art === 'kostenstellen' ? 'Kostenstellenauswertung aus der Kostenrechnung. ' : ''}
                  Analysiert werden BWA Pakete und Jahresabschlüsse.
                </p>
              </div>
            </div>
          </aside>
        )}
      </Geteilt>

      {/* Rueckmeldung zur Analyse: nur, wo es eine gibt oder geben kann. */}
      {analysierbar && <Rueckmeldekasten dokumentId={dokument.id} />}
    </div>
  )
}
